import { baseApi } from "../baseApi";
import { pick } from "../util";
import type { EnterpriseInquiry, SubmitEnterpriseInput } from "../types";

const ENTERPRISE_FIELDS = `
  id
  name
  email
  company
  phone
  country
  message
  status
  createdAt
  updatedAt
`;

export const enterpriseApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    // Authenticated — platform owner reviews inquiries.
    getEnterpriseInquiries: builder.query<EnterpriseInquiry[], void>({
      query: () => ({
        body: {
          query: `
            query EnterpriseInquiries {
              enterpriseInquiries {
                ${ENTERPRISE_FIELDS}
              }
            }
          `,
        },
      }),

      transformResponse: (res: unknown) =>
        pick<EnterpriseInquiry[]>(res, "enterpriseInquiries"),

      providesTags: (result) =>
        result
          ? [
              ...result.map((i) => ({ type: "Enterprise" as const, id: i.id })),
              { type: "Enterprise" as const, id: "LIST" },
            ]
          : [{ type: "Enterprise" as const, id: "LIST" }],
    }),

    // Public — submitted from the landing page.
    submitEnterpriseInquiry: builder.mutation<
      EnterpriseInquiry,
      SubmitEnterpriseInput
    >({
      query: (input) => ({
        body: {
          query: `
            mutation SubmitEnterpriseInquiry($input: SubmitEnterpriseInput!) {
              submitEnterpriseInquiry(input: $input) {
                ${ENTERPRISE_FIELDS}
              }
            }
          `,
          variables: { input },
        },
      }),

      transformResponse: (res: unknown) =>
        pick<EnterpriseInquiry>(res, "submitEnterpriseInquiry"),

      invalidatesTags: [{ type: "Enterprise", id: "LIST" }],
    }),

    updateEnterpriseInquiry: builder.mutation<
      EnterpriseInquiry,
      { id: string; status: string }
    >({
      query: ({ id, status }) => ({
        body: {
          query: `
            mutation UpdateEnterpriseInquiry($id: String!, $status: String!) {
              updateEnterpriseInquiry(id: $id, status: $status) {
                ${ENTERPRISE_FIELDS}
              }
            }
          `,
          variables: { id, status },
        },
      }),

      transformResponse: (res: unknown) =>
        pick<EnterpriseInquiry>(res, "updateEnterpriseInquiry"),

      invalidatesTags: (_result, _err, { id }) => [
        { type: "Enterprise", id },
        { type: "Enterprise", id: "LIST" },
      ],
    }),
  }),
});

export const {
  useGetEnterpriseInquiriesQuery,
  useLazyGetEnterpriseInquiriesQuery,
  useSubmitEnterpriseInquiryMutation,
  useUpdateEnterpriseInquiryMutation,
} = enterpriseApi;
